import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Button, Form } from 'react-bootstrap';
import { fetchCategorias, fetchInformacoesEcommerce, fetchFornecedoresEcommerce, fetchDateRangeEcommerce, fetchDatesWithSalesEcommerce } from '../../services/api';
import DatePicker from '../DatePicker/DatePicker';
import styles from './FiltrosEcommerce.module.css';

const FiltrosEcommerce = ({ onFilterChange, onSearch, onExportCSV, initialCategoria = '', initialInformacao = '', initialFornecedor = '', initialOrdenacao = '', initialDataInicio = '', initialDataFim = '', initialSearch = '', initialMediaDia = '', initialCodigosLote = '', showInformacao = true, showOrdenacao = true }) => {
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(initialCategoria);
  const [informacaoSelecionada, setInformacaoSelecionada] = useState(initialInformacao);
  // Suporta string legada ou array de fornecedores
  const [fornecedoresSelecionados, setFornecedoresSelecionados] = useState(
    initialFornecedor ? (Array.isArray(initialFornecedor) ? initialFornecedor : [initialFornecedor]) : []
  );
  const [fornecedorDropdownOpen, setFornecedorDropdownOpen] = useState(false);
  const [fornecedorBusca, setFornecedorBusca] = useState('');
  const fornecedorSearchRef = useRef(null);
  const fornecedorDropdownRef = useRef(null);
  const [ordenacaoSelecionada, setOrdenacaoSelecionada] = useState(initialOrdenacao);
  const [dataInicio, setDataInicio] = useState(initialDataInicio);
  const [dataFim, setDataFim] = useState(initialDataFim);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [mediaDia, setMediaDia] = useState(initialMediaDia);
  const [dateRange, setDateRange] = useState({ min: '', max: '' });
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [codigosLote, setCodigosLote] = useState('');
  const [loteAtivo, setLoteAtivo] = useState(false);
  const [datasComVendas, setDatasComVendas] = useState([]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fornecedorDropdownRef.current && !fornecedorDropdownRef.current.contains(event.target)) {
        setFornecedorDropdownOpen(false);
        setFornecedorBusca('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focar no campo de busca ao abrir o dropdown
  useEffect(() => {
    if (fornecedorDropdownOpen && fornecedorSearchRef.current) {
      setTimeout(() => fornecedorSearchRef.current?.focus(), 50);
    }
  }, [fornecedorDropdownOpen]);

  // Sincronizar estados internos quando as props iniciais mudarem (para restaurar filtros salvos)
  useEffect(() => {
    setCategoriaSelecionada(initialCategoria);
  }, [initialCategoria]);

  useEffect(() => {
    setInformacaoSelecionada(initialInformacao);
  }, [initialInformacao]);

  useEffect(() => {
    // Suportar tanto string quanto array
    if (initialFornecedor) {
      if (Array.isArray(initialFornecedor)) {
        setFornecedoresSelecionados(initialFornecedor);
      } else if (typeof initialFornecedor === 'string') {
        // Se for string, verifica se tem vírgula
        setFornecedoresSelecionados(initialFornecedor.includes(',') ? initialFornecedor.split(',') : [initialFornecedor]);
      } else {
        setFornecedoresSelecionados([initialFornecedor]);
      }
    } else {
      setFornecedoresSelecionados([]);
    }
  }, [initialFornecedor]);

  useEffect(() => {
    setOrdenacaoSelecionada(initialOrdenacao);
  }, [initialOrdenacao]);

  useEffect(() => {
    setDataInicio(initialDataInicio);
  }, [initialDataInicio]);

  useEffect(() => {
    setDataFim(initialDataFim);
  }, [initialDataFim]);

  useEffect(() => {
    setSearchTerm(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setMediaDia(initialMediaDia);
  }, [initialMediaDia]);

  // Sincroniza o campo de Lote quando os códigos são aplicados de FORA (ex.: botão
  // "Filtrar no catálogo" das Compras Automáticas) — mantém o textarea e o selo "Lote (N)"
  // coerentes com o filtro que de fato está no ar (e permite limpar normalmente).
  useEffect(() => {
    setCodigosLote(initialCodigosLote || '');
    setLoteAtivo(!!(initialCodigosLote && initialCodigosLote.trim()));
  }, [initialCodigosLote]);

  // Debounce para a busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch) onSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, onSearch]);

  // Buscar range de datas disponíveis
  useEffect(() => {
    const loadDateRange = async () => {
      const result = await fetchDateRangeEcommerce();
      if (result.success) {
        setDateRange({
          min: result.data.dataMinima,
          max: result.data.dataMaxima
        });
      }
    };
    loadDateRange();
  }, []);

  // Buscar datas com vendas (para desabilitar dias sem vendas no DatePicker)
  useEffect(() => {
    const loadDatasComVendas = async () => {
      const result = await fetchDatesWithSalesEcommerce();
      if (result.success && result.data) {
        setDatasComVendas(result.data);
      }
    };
    loadDatasComVendas();
  }, []);

  // Buscar categorias
  const { data: categoriasData } = useQuery({
    queryKey: ['categorias'],
    queryFn: fetchCategorias,
    staleTime: 1000 * 60 * 60 // 1 hora
  });

  // Buscar informações
  const { data: informacoesData } = useQuery({
    queryKey: ['informacoes-ecommerce'],
    queryFn: fetchInformacoesEcommerce,
    staleTime: 1000 * 60 * 60 // 1 hora
  });

  // Buscar fornecedores
  const { data: fornecedoresData } = useQuery({
    queryKey: ['fornecedores-ecommerce'],
    queryFn: fetchFornecedoresEcommerce,
    staleTime: 1000 * 60 * 60 // 1 hora
  });

  const categorias = categoriasData?.data || [];

  // Informações: Combinar dados da API com opções padrão
  const informacoesApi = informacoesData?.data || [];
  const informacoesPadrao = ['Recorrente', 'Não Comprar', 'Duplicado'];
  const informacoes = [...new Set([...informacoesPadrao, ...informacoesApi])];

  const fornecedores = fornecedoresData?.data || [];

  const emitFilterChange = (updates = {}) => {
    // Converter array de fornecedores em string separada por vírgula para o backend
    const fornecedorValue = updates.fornecedor ?? fornecedoresSelecionados;
    const fornecedorString = Array.isArray(fornecedorValue) ? fornecedorValue.join(',') : fornecedorValue;

    onFilterChange({
      categoria: updates.categoria ?? categoriaSelecionada,
      informacao: updates.informacao ?? informacaoSelecionada,
      fornecedor: fornecedorString,
      ordenacao: updates.ordenacao ?? ordenacaoSelecionada,
      dataInicio: updates.dataInicio ?? dataInicio,
      dataFim: updates.dataFim ?? dataFim,
      mediaDia: updates.mediaDia ?? mediaDia
    });
  };

  const handleMediaDiaChange = (e) => {
    const valor = (e.target.value || '').replace(/[^0-9]/g, '');
    setMediaDia(valor);
    emitFilterChange({ mediaDia: valor });
  };

  const handleCategoriaChange = (e) => {
    const valor = e.target.value;
    setCategoriaSelecionada(valor);
    emitFilterChange({ categoria: valor });
  };

  const handleInformacaoChange = (e) => {
    const valor = e.target.value;
    setInformacaoSelecionada(valor);
    emitFilterChange({ informacao: valor });
  };

  // Handler para checkbox de fornecedor
  const handleFornecedorToggle = (fornecedor) => {
    setFornecedoresSelecionados(prev => {
      const newSelection = prev.includes(fornecedor)
        ? prev.filter(f => f !== fornecedor)
        : [...prev, fornecedor];

      // Emitir mudança após atualizar estado
      setTimeout(() => emitFilterChange({ fornecedor: newSelection }), 0);
      return newSelection;
    });
  };

  // Limpar todos os fornecedores selecionados
  const handleLimparFornecedores = () => {
    setFornecedoresSelecionados([]);
    emitFilterChange({ fornecedor: [] });
  };

  // Selecionar todos os fornecedores
  const handleSelecionarTodos = () => {
    setFornecedoresSelecionados([...fornecedores]);
    emitFilterChange({ fornecedor: fornecedores });
  };

  const handleOrdenacaoChange = (e) => {
    const valor = e.target.value;
    setOrdenacaoSelecionada(valor);
    emitFilterChange({ ordenacao: valor });
  };

  const handleDataInicioChange = (valor) => {
    setDataInicio(valor);
    // Só emite mudança se também tiver data fim, ou se estiver limpando
    if (dataFim || !valor) {
      emitFilterChange({ dataInicio: valor });
    }
  };

  const handleDataFimChange = (valor) => {
    setDataFim(valor);
    // Só emite mudança se também tiver data inicio, ou se estiver limpando
    if (dataInicio || !valor) {
      emitFilterChange({ dataFim: valor });
    }
  };



  const handleLimpar = () => {
    setCategoriaSelecionada('');
    setInformacaoSelecionada('');
    setFornecedoresSelecionados([]);
    setOrdenacaoSelecionada('');
    setDataInicio('');
    setDataFim('');
    setSearchTerm('');
    setMediaDia('');
    if (onSearch) onSearch('');
    onFilterChange({ categoria: '', informacao: '', fornecedor: '', ordenacao: '', dataInicio: '', dataFim: '', mediaDia: '' });
  };

  const temFiltrosAtivos = categoriaSelecionada || informacaoSelecionada || fornecedoresSelecionados.length > 0 || ordenacaoSelecionada || dataInicio || dataFim || mediaDia || loteAtivo;
  const temPeriodoCompleto = dataInicio && dataFim;

  const handleAplicarLote = () => {
    const codigos = codigosLote
      .split('\n')
      .map(c => c.trim())
      .filter(c => c.length > 0)
      .map(c => {
        // Se for apenas números e tiver menos de 6 dígitos, adiciona zeros à esquerda
        if (/^\d+$/.test(c) && c.length < 6) {
          return c.padStart(6, '0');
        }
        return c;
      });

    if (codigos.length > 0) {
      setLoteAtivo(true);
      const fornecedorString = fornecedoresSelecionados.join(',');
      onFilterChange({
        categoria: categoriaSelecionada,
        informacao: informacaoSelecionada,
        fornecedor: fornecedorString,
        ordenacao: ordenacaoSelecionada,
        dataInicio: dataInicio,
        dataFim: dataFim,
        codigosLote: codigos
      });
    }
    setShowLoteModal(false);
  };

  const handleLimparLote = () => {
    setCodigosLote('');
    setLoteAtivo(false);
    const fornecedorString = fornecedoresSelecionados.join(',');
    onFilterChange({
      categoria: categoriaSelecionada,
      informacao: informacaoSelecionada,
      fornecedor: fornecedorString,
      ordenacao: ordenacaoSelecionada,
      dataInicio: dataInicio,
      dataFim: dataFim,
      codigosLote: []
    });
    setShowLoteModal(false);
  };

  return (
    <div className={styles.filterBarContainer}>
      <div className={styles.filterGroup}>
        <div className={styles.filterItem} ref={fornecedorDropdownRef} style={{ position: 'relative' }}>
          <label className={styles.filterLabel}>Fornecedor</label>
          <div
            className={`${styles.filterSelect} ${styles.multiSelectTrigger}`}
            onClick={() => setFornecedorDropdownOpen(!fornecedorDropdownOpen)}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fornecedoresSelecionados.length === 0
                ? 'Todos'
                : fornecedoresSelecionados.length === 1
                  ? fornecedoresSelecionados[0]
                  : `${fornecedoresSelecionados.length} selecionados`}
            </span>
            <span style={{ marginLeft: '8px' }}>{fornecedorDropdownOpen ? '▲' : '▼'}</span>
          </div>
          {fornecedorDropdownOpen && (
            <div className={styles.multiSelectDropdown}>
              <div className={styles.multiSelectSearch}>
                <input
                  ref={fornecedorSearchRef}
                  type="text"
                  className={styles.multiSelectSearchInput}
                  placeholder="Buscar fornecedor..."
                  value={fornecedorBusca}
                  onChange={(e) => setFornecedorBusca(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {fornecedorBusca && (
                  <button
                    className={styles.multiSelectSearchClear}
                    onClick={(e) => { e.stopPropagation(); setFornecedorBusca(''); }}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className={styles.multiSelectActions}>
                <button type="button" onClick={handleSelecionarTodos} className={styles.multiSelectActionBtn}>
                  Selecionar Todos
                </button>
                <button type="button" onClick={handleLimparFornecedores} className={styles.multiSelectActionBtn}>
                  Limpar
                </button>
              </div>
              <div className={styles.multiSelectList}>
                {fornecedores
                  .filter(f => !fornecedorBusca || f.toLowerCase().includes(fornecedorBusca.toLowerCase()))
                  .map((f) => (
                    <label key={f} className={styles.multiSelectItem}>
                      <input
                        type="checkbox"
                        checked={fornecedoresSelecionados.includes(f)}
                        onChange={() => handleFornecedorToggle(f)}
                      />
                      <span>{f}</span>
                    </label>
                  ))}
                {fornecedorBusca && fornecedores.filter(f => f.toLowerCase().includes(fornecedorBusca.toLowerCase())).length === 0 && (
                  <div className={styles.multiSelectEmpty}>Nenhum fornecedor encontrado</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Categoria</label>
          <select
            value={categoriaSelecionada}
            onChange={handleCategoriaChange}
            className={styles.filterSelect}
          >
            <option value="">Todas</option>
            {categorias.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {showInformacao && (
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>Informação</label>
            <select
              value={informacaoSelecionada}
              onChange={handleInformacaoChange}
              className={styles.filterSelect}
            >
              <option value="">Status</option>
              {informacoes.map((i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
        )}

        {showOrdenacao && (
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>Ordenação</label>
            <select
              value={ordenacaoSelecionada}
              onChange={handleOrdenacaoChange}
              className={styles.filterSelect}
            >
              <option value="">Padrão</option>
              <option value="total_vendas">Total Vendas</option>
              <option value="mais_vendidos_mes">Mais Vendidos Mês</option>
              <option value="menor_estoque">Menor Estoque</option>
              <option value="maior_estoque">Maior Estoque</option>
              <option value="maior_lucro">Maior Lucro</option>
              <option value="maior_markup">Maior Markup</option>
              <option value="menor_markup">Menor Markup</option>
              <option value="maior_media_dia">Maior Média Dia</option>
              <option value="menor_media_dia">Menor Média Dia</option>
              <option value="nao_vendidos">Não Vendidos no Período</option>
            </select>
          </div>
        )}

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Período De</label>
          <DatePicker
            value={dataInicio}
            onChange={handleDataInicioChange}
            placeholder="Data início"
            availableDates={datasComVendas}
          />
        </div>

        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>Até</label>
          <DatePicker
            value={dataFim}
            onChange={handleDataFimChange}
            placeholder="Data fim"
            availableDates={datasComVendas}
          />
        </div>
        <div className={styles.filterItem} style={{ minWidth: '110px' }}>
          <label className={styles.filterLabel}>Média Dia</label>
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={mediaDia}
            onChange={handleMediaDiaChange}
            className={styles.filterInput}
            placeholder="ex: 3"
            title="Filtra produtos com média diária de N até N,9 (ex.: 3 → 3,0 a 3,9)"
          />
        </div>
        <div className={styles.filterItem} style={{ minWidth: '200px', flexGrow: 2 }}>
          <label className={styles.filterLabel}>Busca</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.filterInput}
            placeholder="Buscar produtos..."
          />
        </div>
      </div>

      <button
        onClick={() => setShowLoteModal(true)}
        className={`${styles.loteButton} ${loteAtivo ? styles.loteButtonActive : ''}`}
        title="Busca em Lote - colar vários códigos"
      >
        📋 {loteAtivo ? `Lote (${codigosLote.split('\n').filter(c => c.trim()).length})` : 'Lote'}
      </button>

      {onExportCSV && (
        <button
          onClick={onExportCSV}
          className={styles.exportButton}
          title="Exportar produtos listados para CSV"
        >
          📥 CSV
        </button>
      )}

      {(temFiltrosAtivos || searchTerm) && (
        <button onClick={handleLimpar} className={styles.clearButton} title="Limpar Filtros e Busca">
          <span className={styles.clearIcon}>✕</span>
        </button>
      )}

      {/* Modal de Busca em Lote */}
      <Modal show={showLoteModal} onHide={() => setShowLoteModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#1e3a5f', color: 'white' }}>
          <Modal.Title>📋 Busca em Lote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Cole ou digite os códigos dos produtos (um por linha):</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={codigosLote}
              onChange={(e) => setCodigosLote(e.target.value)}
              placeholder="Exemplo:\nCOD001\nCOD002\nCOD003"
              style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
            />
          </Form.Group>
          <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
            {codigosLote.split('\n').filter(c => c.trim()).length} código(s) detectado(s)
          </div>
        </Modal.Body>
        <Modal.Footer>
          {loteAtivo && (
            <Button variant="outline-danger" onClick={handleLimparLote}>
              Limpar Lote
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowLoteModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleAplicarLote}>
            Aplicar Filtro
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FiltrosEcommerce;

